import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {SubirEscaneosControl} from '@/components/bateria/subir-escaneos-control'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import {useQuitarValidacion} from '@/hooks/bateria/use-bateria-cola'
import {publicarAviso} from '@/lib/avisos'
import {detalleDelError, translateApiError} from '@/lib/errors'
import {esIncompleto, esListoParaValidar, type LotesResumen} from '@/lib/bateria'
import {cn} from '@/lib/utils'

/** Los dos 409 son carreras con su propia salida. El de evaluación cerrada se traduce acá porque el
 * copy global de ese `detail` habla de eliminar la batería. */
function textoDelFallo(err: unknown, t: (key: string) => string): string {
  // Primero `translateApiError`: marca el error comunicado y la red global no publica otra burbuja.
  const generico = translateApiError(err)
  const detail = detalleDelError(err)
  if (detail === 'the evaluación is closed and can no longer be modified') {
    return t('admin.bateria.detail.quitarValidacionCerrada')
  }
  if (detail === 'the paquete is not validado') return t('admin.bateria.detail.quitarValidacionYaNoEstaba')
  return generico
}

/** Sin estado que la admita se deshabilita con el motivo escrito, nunca se esconde. El motivo no va en
 * `title`: Radix pone `pointer-events: none` en un ítem deshabilitado y el tooltip nunca abriría. */
function AccionDeMenu({
  testId,
  rotulo,
  motivo,
  onSelect,
}: {
  testId: string
  rotulo: string
  motivo?: string
  onSelect: () => void
}) {
  return (
    <DropdownMenuItem
      data-testid={testId}
      disabled={motivo !== undefined}
      onSelect={onSelect}
      className={cn(motivo !== undefined ? 'flex-col items-start gap-0.5 py-1' : 'py-1.5')}>
      <span>{rotulo}</span>
      {motivo !== undefined && (
        <span className="max-w-full text-[10px] leading-tight text-muted-foreground">{motivo}</span>
      )}
    </DropdownMenuItem>
  )
}

/** El padre es dueño de todo el estado; esta celda solo dispara los callbacks. */
export function PersonaAccionesCelda({
  paquete: p,
  businessId,
  evaluacionId,
  lotes,
  batchRunning,
  readOnly,
  validarPending,
  onValidar,
  onRevisar,
  onAbrirEnCola,
  onDigitar,
  onVerResultado,
  onVerFicha,
  onImprimir,
  onReprocesar,
  onReasignar,
  onCorregirIdentidad,
  onCambiarForma,
  onRetirar,
  onVerHistorialPaginas,
}: {
  paquete: BateriaPaquete
  businessId: string
  evaluacionId: string
  lotes: LotesResumen
  batchRunning: boolean
  readOnly: boolean
  validarPending: boolean
  onValidar: (p: BateriaPaquete) => void
  /** La revisión primaria (diseño v2 §8.2): la cola con `?paquete_id=`, acotada a sus dudas. */
  onRevisar: (p: BateriaPaquete) => void
  /** La misma cola con `filtros=1`, para seguir revisando a esta persona sea cual sea su estado. */
  onAbrirEnCola: (p: BateriaPaquete) => void
  onDigitar: (p: BateriaPaquete) => void
  onVerResultado: (p: BateriaPaquete) => void
  onVerFicha: (p: BateriaPaquete) => void
  onImprimir: (p: BateriaPaquete) => void
  onReprocesar: (p: BateriaPaquete) => void
  onReasignar: (p: BateriaPaquete) => void
  onCorregirIdentidad: (p: BateriaPaquete) => void
  onCambiarForma: (p: BateriaPaquete) => void
  onRetirar: (p: BateriaPaquete) => void
  onVerHistorialPaginas: (p: BateriaPaquete) => void
}) {
  const {t} = useTranslation()
  const quitarValidacion = useQuitarValidacion(businessId, evaluacionId)
  // El mismo predicado que el Tag «Listo para validar»: la fila no puede ofrecer «Validar» junto a
  // un badge que diga «Por revisar».
  const puedeValidar = !readOnly && esListoParaValidar(p)
  const puedeRevisar = !readOnly && p.estado === 'transcrito' && p.pendientes_de_decision > 0
  const incompleto = esIncompleto(p, batchRunning)
  // Adenda §7: re-procesar un paquete ya leído es un flujo válido, no solo un accidente.
  const puedeReprocesar = !readOnly && (p.estado === 'transcrito' || p.estado === 'revisado' || p.estado === 'validado')

  return (
    <div className="flex flex-col items-end gap-1" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-end gap-1">
        {/* Validar y revisar se excluyen por estado: un solo primario deja la fila en dos botones (R2). */}
        {(puedeValidar || puedeRevisar) && (
          <Button
            size="xs"
            variant={puedeValidar ? 'default' : 'outline'}
            data-testid={puedeValidar ? `validar-${p.id}` : `revisar-${p.id}`}
            disabled={puedeValidar && validarPending}
            onClick={puedeValidar ? () => onValidar(p) : () => onRevisar(p)}>
            {puedeValidar
              ? validarPending
                ? t('admin.bateria.detail.validando')
                : t('admin.bateria.detail.validar')
              : t('admin.bateria.detail.revisarDudas', {count: p.pendientes_de_decision})}
          </Button>
        )}
        {/* Renders null outside generado/error and manages its own file input and progress. */}
        {!readOnly && (
          <SubirEscaneosControl
            businessId={businessId}
            evaluacionId={evaluacionId}
            paquete={p}
            lotes={lotes}
            variant="paquete"
          />
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-xs"
              variant="ghost"
              data-testid={`acciones-${p.id}`}
              aria-label={t('admin.bateria.detail.masAcciones')}>
              <span aria-hidden="true" className="text-base leading-none">
                ⋯
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64">
            <DropdownMenuLabel className="pt-1.5 pb-0.5">{t('admin.bateria.detail.grupo.revision')}</DropdownMenuLabel>
            {/* Nunca comparte `testid` con la primaria: las dos pueden coexistir. */}
            <AccionDeMenu
              testId={`abrir-en-cola-${p.id}`}
              rotulo={t('admin.bateria.detail.abrirEnLaCola')}
              onSelect={() => onAbrirEnCola(p)}
            />
            {/* Sin confirmación a propósito: no destruye nada y se deshace volviendo a validar. */}
            {!readOnly && (
              <AccionDeMenu
                testId={`quitar-validacion-${p.id}`}
                rotulo={t('admin.bateria.detail.quitarValidacion')}
                // Su propio motivo: el de «Ver resultado» sugeriría que quitarla destraba el resultado.
                motivo={p.estado === 'validado' ? undefined : t('admin.bateria.detail.porque.sinValidacionQueQuitar')}
                onSelect={() =>
                  quitarValidacion.mutate(p.id, {
                    // Su propio `onError`: la red global, por el `detail` compartido, hablaría de eliminar.
                    onError: (err) =>
                      publicarAviso(t('admin.bateria.detail.quitarValidacion'), textoDelFallo(err, t), {
                        testId: `quitar-validacion-error-${p.id}`,
                      }),
                  })
                }
              />
            )}

            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className="pt-1.5 pb-0.5">{t('admin.bateria.detail.grupo.papel')}</DropdownMenuLabel>
            <AccionDeMenu
              testId={`imprimir-${p.id}`}
              rotulo={t('admin.bateria.detail.imprimir')}
              onSelect={() => onImprimir(p)}
            />
            {!readOnly && (
              <AccionDeMenu
                testId={`reprocesar-abrir-${p.id}`}
                rotulo={t('admin.bateria.detail.reprocesar')}
                motivo={puedeReprocesar ? undefined : t('admin.bateria.detail.porque.sinLectura')}
                onSelect={() => onReprocesar(p)}
              />
            )}
            {!readOnly && (
              <AccionDeMenu
                testId={`digitar-${p.id}`}
                rotulo={t('admin.bateria.detail.digitar')}
                motivo={p.estado === 'validado' ? t('admin.bateria.detail.porque.yaValidado') : undefined}
                onSelect={() => onDigitar(p)}
              />
            )}
            {!readOnly && (
              <AccionDeMenu
                testId={`cambiar-forma-${p.id}`}
                rotulo={t('admin.bateria.detail.cambiarForma')}
                onSelect={() => onCambiarForma(p)}
              />
            )}

            <DropdownMenuSeparator className="my-1" />
            <DropdownMenuLabel className="pt-1.5 pb-0.5">{t('admin.bateria.detail.grupo.laPersona')}</DropdownMenuLabel>
            <AccionDeMenu
              testId={`ficha-${p.id}`}
              rotulo={t('admin.bateria.detail.ficha')}
              onSelect={() => onVerFicha(p)}
            />
            {/* Resultado completo por empleado: solo admin, custodia clínica de Inci. */}
            <AccionDeMenu
              testId={`ver-resultado-${p.id}`}
              rotulo={t('admin.bateria.detail.verResultado')}
              motivo={p.estado === 'validado' ? undefined : t('admin.bateria.detail.porque.sinValidar')}
              onSelect={() => onVerResultado(p)}
            />
            {!readOnly && (
              <AccionDeMenu
                testId={`reasignar-${p.id}`}
                rotulo={t('admin.bateria.detail.reasignar')}
                onSelect={() => onReasignar(p)}
              />
            )}
            {!readOnly && (
              <AccionDeMenu
                testId={`corregir-identidad-${p.id}`}
                rotulo={t('admin.bateria.detail.corregirIdentidad')}
                onSelect={() => onCorregirIdentidad(p)}
              />
            )}
            {/* Sola tras su separador: es la única acción que saca a alguien del alcance. */}
            {!readOnly && (
              <>
                <DropdownMenuSeparator className="my-1" />
                <AccionDeMenu
                  testId={`retirar-${p.id}`}
                  rotulo={t('admin.bateria.detail.retirar')}
                  onSelect={() => onRetirar(p)}
                />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Incompleto (v2 §2.3 op. 11): inline, sin pasar por el kebab. */}
      {incompleto && (
        <SubirEscaneosControl
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={p}
          lotes={lotes}
          variant="paquete"
          forzar
          label={t('admin.bateria.detail.subirLasQueFaltan')}
        />
      )}
      {/* Reemplazos (G29′, adenda §7.2). */}
      {p.paginas_reemplazadas.length > 0 && (
        <button
          type="button"
          data-testid={`reemplazos-aviso-${p.id}`}
          onClick={() => onVerHistorialPaginas(p)}
          className="text-right text-[11px] text-primary hover:underline">
          {t('admin.bateria.detail.reemplazosAviso', {count: p.paginas_reemplazadas.length})}
        </button>
      )}
    </div>
  )
}
